import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useOrgId } from '@/hooks/useOrgId';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionsContext, type PermissionsContextValue } from '@/lib/permissionsContext';
import { fetchEffectivePermissions, hasPermission as checkPermission } from '@/lib/permissionUtils';

export const PermissionsProvider = ({
  children,
  orgId,
}: {
  children: ReactNode;
  orgId?: string | null;
}) => {
  const { user } = useAuth();
  const { orgId: resolvedOrgId, isLoading: orgLoading } = useOrgId();
  const activeOrgId = orgId ?? resolvedOrgId;
  const { data, isLoading } = useQuery({
    queryKey: ['effective-permissions', user?.id, activeOrgId],
    queryFn: async () => {
      if (!user) return [];
      if (!activeOrgId) return [];
      return fetchEffectivePermissions(activeOrgId, user.id);
    },
    enabled: !!user,
    staleTime: 30_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    refetchInterval: 30_000,
  });

  const permissions = useMemo(() => data ?? [], [data]);
  const isSuperAdmin = permissions.includes('*');

  const value = useMemo<PermissionsContextValue>(
    () => ({
      permissions,
      isSuperAdmin,
      isLoading: isLoading || orgLoading,
      hasPermission: (perm) => checkPermission(permissions, perm),
    }),
    [permissions, isSuperAdmin, isLoading, orgLoading]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const Can = ({
  perm,
  fallback = null,
  children,
}: {
  perm: string;
  fallback?: ReactNode;
  children: ReactNode;
}) => {
  const { hasPermission: can, isLoading } = usePermissions();
  if (isLoading) return null;
  if (!can(perm)) return <>{fallback}</>;
  return <>{children}</>;
};
